import { IUser } from "@/common/services/types";
import { deviceTypeEnum } from "@/common/utils/device";
import { SearchInitiator } from "@/common/store/analyticsStore";

export enum EventSource {
  Collections = "collections",
  Embed = "embed",
  Explore = "explore",
  Favourites = "favourites",
  ForYou = "foryou",
  Main = "main",
  Niche = "niche",
  Other = "other",
  Profile = "profile",
  DirectWatch = "direct",
  Search = "search",
  SearchGifsTop = "search gifs top",
  SearchGifsTrending = "search gifs trending",
  SearchGifsLatest = "search gifs latest",
  SearchGifsHot = "search gifs hot",
  SearchGifsMostRelevant = "search gifs most relevant",
}

export enum SearchResultSection {
  Gif = "gif",
  Image = "image",
  Niche = "niche",
  Creator = "creator",
  Tag = "tag",
}

export interface GifViewEvent {
  gifId: string;
  tags: string[];
  source: EventSource;
  sourceId: string | null;
  position: number;
  boosted: boolean;
  userName?: string | null;
  fullscreen: boolean;
  orientation?: string;
  context?: string | null;
  reason?: string | null;
}

export interface MeaningfulGifViewEvent {
  gifId: string;
  percent: number;
}

export interface WatchPageNavigationEvent {
  elementType: string;
  targetURL: string;
}

export interface GifPlaybackEvent {
  duration: number;
  gif: string;
  gifDuration?: number;
  source: EventSource;
  sourceId: string | null;
  userName?: string | null;
  percent: number;
  boosted: boolean;
  position?: number | null;
  playCount: number;
  startPoint: number;
}

export interface GifReplayEvent {
  gifId: string;
  tags: string[];
}

export interface UploadStartedEvent {
  verified: boolean;
  username: string;
}

export interface UploadEndedEvent {
  verified: boolean;
  username: string;
}

export interface UploadSelectedEvent {
  type: "image" | "video" | "bulkImages" | "url";
  verified: boolean;
  username: string;
}

export interface UploadSourceConfirmedEvent {
  type: "image" | "video" | "multiple";
  verified: boolean;
  username: string;
}

export interface UploadSourceDroppedEvent {
  type: "image" | "video" | "images";
  verified: boolean;
  username: string;
}

export interface UploadAudienceDefinedEvent {
  verified: boolean;
  username: string;
}

export interface UploadVideoTrimmedEvent {
  verified: boolean;
  username: string;
}

export interface UploadTagSelectionStartedEvent {
  verified: boolean;
  username: string;
}

export interface UploadTagSelectionEndedEvent {
  count: number;
  verified: boolean;
  username: string;
}

export interface GifClickedEvent {
  source: EventSource;
  position: number;
}

export interface GifLikedEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position: number;
  playCount: number;
  isMainGIF: boolean;
  userName?: string | null;
}

export interface GifUnlikedEvent {
  gifId: string;
  isMainGIF: boolean;
}

export interface GifSharedEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position?: number;
  playCount?: number;
  userName?: string | null;
}

export interface AddedToCollectionEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position?: number;
  playCount?: number;
  userName?: string | null;
}

export interface AdLoadedEvent {
  isLoaded: boolean;
}

export interface MenuButtonClickedEvent {
  name: string;
  label: string;
}

export interface FollowUserClickedEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
  gifId?: string;
  position?: number;
  playCount?: number;
}

export interface UnfollowUserClickedEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
}

export interface FollowUserClickedAnonymouslyEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
}

export interface SocialProfileClickedEvent {
  userName: string;
}

export interface ProfileClickedEvent {
  gifId: string;
  creator: string;
  source: EventSource;
  sourceId: string | null;
  position?: number;
  playCount?: number;
  userName?: string | null;
}

export interface TagClickedEvent {
  tag: string;
  section: string;
}

export interface SearchedEvent {
  query: string;
  totalCount: number;
}

export type QueryType = "Manual" | "Tag" | "User" | "Niche" | "Historic";

interface SearchQuerySubmittedEvent {
  query: string;
  queryType: QueryType;
  queryCount: number;
  itemId?: string;
}

export interface UploadFinishedEvent {
  sourceURL?: "local" | "url";
  verified: boolean;
  username: string;
}

export interface ProfileViewedEvent {
  userName: string;
}

export interface UserFetchedEvent {
  user: IUser;
}

export interface ExperimentVariantExposureEvent {
  experimentKey: string;
  experimentVariant: string | boolean;
}

export interface FirstVisitEvent {
  path: string;
  timestamp: number;
}

export interface DailyVisitEvent {
  username?: string;
}

export interface managePageVisitedEvent {
  username: string;
  verified: boolean;
}

export interface DeviceTrackedEvent {
  deviceId: string;
  deviceType: deviceTypeEnum;
}

export interface SearchResultClickedEvent {
  section: SearchResultSection;
  title: string;
  position: number;
  initiator: SearchInitiator | null;
}

export interface SeeAllButtonClickedEvent {
  section?: SearchResultSection;
  initiator: SearchInitiator | null;
}

interface PageClosedEvent {
  page: "watch" | "gifs" | "niche";
  type: string;
  gifsViewed: number;
  gifsViewedMeaningful: number;
  timeSpent: number;
  contentLength: number;
  searchTag?: string;
}

interface OrientationChangedEvent {
  type: string;
}

export interface ViewModeFeedChangedEvent {
  feedName: string;
  viewMode: string;
}

interface QuickRecsClosedEvent {
  type: string;
}

interface QuickRecsItemClickedEvent {
  position: number;
}

export interface NicheGifVoteUpClickedEvent {
  gif: string;
  niche: string;
}

export interface NicheGifVoteDownClickedEvent {
  gif: string;
  niche: string;
}

export interface JoinNicheClickedEvent {
  nicheId: string;
}

export interface JoinNicheClickedAnonymouslyEvent {
  nicheId: string;
}

export interface LeaveNicheClickedEvent {
  nicheId: string;
}

export type NichePreviewClickInitiator = "meta" | "image" | "arrow button" | "watch button" | "join button";

export interface NichePreviewClickedEvent {
  nicheId: string;
  source: EventSource;
  initiator: NichePreviewClickInitiator;
}

export interface ManageItemInteractedEvent {
  id: string;
  interaction: string;
  username: string;
  verified: boolean;
}

export interface ManagePageInteractedEvent {
  interaction: string;
  username: string;
  verified: boolean;
}

export interface BoostProfileClickedEvent {
  campaignId: string;
}

export interface NicheGifsOrderChangedEvent {
  nicheId: string;
  order: string;
}

export interface LiveCircleClickedEvent {
  deviceType: deviceTypeEnum;
}

export interface NicheCategoryClickedEvent {
  category: string;
}

export type BusEvents = {
  gifView: GifViewEvent;
  meaningfulGifView: MeaningfulGifViewEvent;
  gifPlayback: GifPlaybackEvent;
  gifReplay: GifReplayEvent;

  forceFullscreenEntered: undefined;
  forceFullscreenExited: undefined;
  fullscreenEntered: undefined;
  fullscreenExited: undefined;

  watchPageVisited: undefined;
  firstVisit: FirstVisitEvent;
  dailyVisit: DailyVisitEvent;
  homePageVisited: undefined;
  managePageVisited: managePageVisitedEvent;

  gifClicked: GifClickedEvent;
  gifLiked: GifLikedEvent;
  gifUnliked: GifUnlikedEvent;
  gifShared: GifSharedEvent;
  addedToCollection: AddedToCollectionEvent;

  adLoaded: AdLoadedEvent;

  infoBarClicked: undefined;
  infoBarClosed: undefined;

  sharedFixedEmbed: undefined;
  sharedResponsiveEmbed: undefined;
  shareClick: undefined;
  shareCopy: undefined;
  shareDiscord: undefined;
  shareEmbed: undefined;
  shareReddit: undefined;
  shareTwitter: undefined;
  optionClicked: undefined;
  pinnedContentClicked: undefined;
  menuButtonClicked: MenuButtonClickedEvent;
  followUserClicked: FollowUserClickedEvent;
  followUserClickedAnonymously: FollowUserClickedAnonymouslyEvent;
  unfollowUserClicked: UnfollowUserClickedEvent;
  socialProfileClicked: SocialProfileClickedEvent;
  profileClicked: ProfileClickedEvent;
  boostProfileClicked: BoostProfileClickedEvent;
  tagClicked: TagClickedEvent;

  signUpViewed: undefined;
  signUpSuccess: undefined;
  loginCompleted: undefined;
  loggedOut: undefined;

  scrollPromptViewed: undefined;
  scrollPromptClosed: undefined;

  searchBarOpened: undefined;
  searched: SearchedEvent;
  searchResultClicked: SearchResultClickedEvent;
  searchQuerySubmitted: SearchQuerySubmittedEvent;
  seeAllButtonClicked: SeeAllButtonClickedEvent;

  tagFeedbackEnabledOptions: undefined;
  tagFeedbackEnabled: undefined;

  uploadSelected: UploadSelectedEvent;
  uploadStarted: UploadStartedEvent;
  uploadTagSelectionStarted: UploadTagSelectionStartedEvent;
  uploadTagSelectionEnded: UploadTagSelectionEndedEvent;
  uploadEnded: UploadEndedEvent;
  uploadVideoTrimmed: UploadVideoTrimmedEvent;
  uploadGetVerifiedClicked: undefined;
  uploadSourceConfirmed: UploadSourceConfirmedEvent;
  uploadAudienceDefined: UploadAudienceDefinedEvent;
  uploadSourceDropped: UploadSourceDroppedEvent;
  uploadFinished: UploadFinishedEvent;

  gifWatched120s: undefined;
  profileViewed: ProfileViewedEvent;
  errorLoadingGifs: undefined;
  voteUpAddClicked: undefined;
  voteUpRemoveClicked: undefined;
  voteDownAddClicked: undefined;
  voteDownRemoveClicked: undefined;
  privateSessionStarted: undefined;

  PWASessionStarted: undefined;
  PWAPageVisited: undefined;

  userFetched: UserFetchedEvent;
  deviceTracked: DeviceTrackedEvent;

  experimentVariantExposure: ExperimentVariantExposureEvent;

  watchPageNavigation: WatchPageNavigationEvent;

  playerNicheClicked: undefined;
  playerTagClicked: undefined;

  verificationSubmit: undefined;

  pageClosed: PageClosedEvent;

  orientationChanged: OrientationChangedEvent;

  viewModeFeedChanged: ViewModeFeedChangedEvent;

  quickRecsShown: undefined;
  quickRecsClosed: QuickRecsClosedEvent;
  quickRecsItemClicked: QuickRecsItemClickedEvent;

  nicheGifVoteUpClicked: NicheGifVoteUpClickedEvent;
  nicheGifVoteDownClicked: NicheGifVoteDownClickedEvent;
  nicheGifsOrderChanged: NicheGifsOrderChangedEvent;
  joinNicheClicked: JoinNicheClickedEvent;
  joinNicheClickedAnonymously: JoinNicheClickedAnonymouslyEvent;
  leaveNicheClicked: LeaveNicheClickedEvent;
  nichePreviewClicked: NichePreviewClickedEvent;

  manageItemInteracted: ManageItemInteractedEvent;
  managePageInteracted: ManagePageInteractedEvent;

  liveCircleClicked: LiveCircleClickedEvent;
  nicheCategoryClicked: NicheCategoryClickedEvent;
};
